import { useState, useCallback, useRef, useEffect } from "react";
import { api } from "@/api";
import type { AttachedFile } from "@/components/chat/Composer";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: Date;
  model?: string;
  attachments?: AttachedFile[];
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  pinned: boolean;
  archived: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/* ── Persistence ── */

const STORAGE_KEY = "zeus_chat_conversations";
const ACTIVE_KEY = "zeus_chat_active_id";

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function generateTitle(content: string): string {
  const words = content.split(" ").slice(0, 6).join(" ");
  return words.length > 40 ? words.substring(0, 40) + "\u2026" : words;
}

function loadConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Rehydrate Date objects
    return parsed.map((c: Record<string, unknown>) => ({
      ...c,
      createdAt: new Date(c.createdAt as string),
      updatedAt: new Date(c.updatedAt as string),
      messages: ((c.messages as Record<string, unknown>[]) || []).map(
        (m: Record<string, unknown>) => ({
          ...m,
          createdAt: new Date(m.createdAt as string),
        })
      ),
    })) as Conversation[];
  } catch {
    return [];
  }
}

function saveConversations(conversations: Conversation[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  } catch {
    // localStorage full — silently fail
  }
}

function loadActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

function saveActiveId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // silently fail
  }
}

/* ── Hook ── */

export function useChat() {
  const [conversations, setConversations] = useState<Conversation[]>(() =>
    loadConversations()
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(() => {
    const saved = loadActiveId();
    const loaded = loadConversations();
    if (saved && loaded.some((c) => c.id === saved)) return saved;
    return loaded.length > 0 ? loaded[0].id : null;
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  // Persist on change
  useEffect(() => {
    saveConversations(conversations);
  }, [conversations]);
  useEffect(() => {
    saveActiveId(activeConversationId);
  }, [activeConversationId]);

  const activeConversation =
    conversations.find((c) => c.id === activeConversationId) || null;

  const sendMessage = useCallback(
    async (content: string, attachments?: AttachedFile[]) => {
      let convId = activeConversationId;

      // Auto-create conversation if none active
      if (!convId) {
        const newConv: Conversation = {
          id: generateId(),
          title: generateTitle(content),
          messages: [],
          pinned: false,
          archived: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setConversations((prev) => [newConv, ...prev]);
        convId = newConv.id;
        setActiveConversationId(convId);
      }

      const userMsg: Message = {
        id: generateId(),
        role: "user",
        content,
        createdAt: new Date(),
        attachments,
      };

      // Append user message immediately
      const capturedConvId = convId;
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== capturedConvId) return c;
          const isFirst = c.messages.length === 0;
          return {
            ...c,
            title: isFirst ? generateTitle(content) : c.title,
            messages: [...c.messages, userMsg],
            updatedAt: new Date(),
          };
        })
      );

      // Call Zeus API
      setIsStreaming(true);

      try {
        // Build message history
        const current = conversationsRef.current.find(
          (c) => c.id === capturedConvId
        );
        const history = current
          ? [...current.messages, userMsg].map((m) => ({
              role: m.role,
              content: m.content,
            }))
          : [{ role: "user" as const, content }];

        const res = await api.post<{
          reply?: string;
          message?: string;
          response?: string;
        }>("/chat/test", { messages: history });

        const replyText =
          res.ok && res.data
            ? res.data.reply ??
              res.data.message ??
              res.data.response ??
              "No response received."
            : res.error ?? "Something went wrong. Please try again.";

        const assistantMsg: Message = {
          id: generateId(),
          role: "assistant",
          content: replyText,
          createdAt: new Date(),
          model: "Zeus-1",
        };

        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== capturedConvId) return c;
            return {
              ...c,
              messages: [...c.messages, assistantMsg],
              updatedAt: new Date(),
            };
          })
        );
      } catch {
        const errorMsg: Message = {
          id: generateId(),
          role: "assistant",
          content:
            "Failed to get a response. Please check your connection and try again.",
          createdAt: new Date(),
        };

        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== capturedConvId) return c;
            return {
              ...c,
              messages: [...c.messages, errorMsg],
              updatedAt: new Date(),
            };
          })
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [activeConversationId]
  );

  const deleteConversation = useCallback(
    (id: string) => {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConversationId === id) setActiveConversationId(null);
    },
    [activeConversationId]
  );

  const renameConversation = useCallback((id: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title } : c))
    );
  }, []);

  const pinConversation = useCallback((id: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, pinned: !c.pinned } : c))
    );
  }, []);

  const archiveConversation = useCallback((id: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, archived: true } : c))
    );
  }, []);

  const selectConversation = useCallback((id: string) => {
    setActiveConversationId(id);
  }, []);

  const startNewChat = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  return {
    conversations,
    activeConversation,
    activeConversationId,
    isStreaming,
    sendMessage,
    deleteConversation,
    renameConversation,
    pinConversation,
    archiveConversation,
    selectConversation,
    startNewChat,
  };
}
